import { createTheme, Theme } from '@mui/material';

export type ThemeMode = 'dark' | 'light';

const cache: Partial<Record<ThemeMode, Theme>> = {};

export function getTheme(mode: ThemeMode): Theme {
  if (cache[mode]) return cache[mode] as Theme;
  const dark = mode === 'dark';
  const surface = dark ? '#2d2d2d' : '#ffffff';
  const surfaceAlt = dark ? '#3a3a3a' : '#f3f0ff';
  const border = dark ? '#404040' : '#d9d4ee';

  const theme = createTheme({
    palette: {
      mode,
      primary: { main: dark ? '#9f7bff' : '#5b2fd6' },
      secondary: { main: dark ? '#ff6fa3' : '#c2185b' },
      background: { default: dark ? '#1a1a1a' : '#f6f4fc', paper: surface },
      text: dark
        ? { primary: '#ffffff', secondary: '#c4c4c4' }
        : { primary: '#1d1b26', secondary: '#55526a' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: surface,
            backgroundImage: `linear-gradient(45deg, ${surface} 0%, ${surfaceAlt} 100%)`,
            color: dark ? '#ffffff' : '#1d1b26',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: surface,
            backgroundImage: `linear-gradient(145deg, ${surface} 0%, ${surfaceAlt} 100%)`,
            border: `1px solid ${border}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: surface,
            backgroundImage: `linear-gradient(145deg, ${surface} 0%, ${surfaceAlt} 100%)`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: '8px', minHeight: 40 },
          contained: {
            backgroundImage: 'linear-gradient(45deg, #5b2fd6 30%, #8e24aa 90%)',
            color: '#ffffff',
            boxShadow: '0 3px 5px 2px rgba(124, 77, 255, .3)',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse), (max-width: 599.95px)': { minWidth: 44, minHeight: 44 },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: dark ? '#404040' : '#e6e0fa',
            color: dark ? '#ffffff' : '#1d1b26',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '& .MuiTabs-indicator': { height: '3px' },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 48,
            color: dark ? '#c4c4c4' : '#55526a',
            '&.Mui-selected': { color: dark ? '#b79cff' : '#5b2fd6' },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `3px solid ${dark ? '#b79cff' : '#5b2fd6'}`,
              outlineOffset: 2,
            },
          },
        },
      },
    },
  });
  cache[mode] = theme;
  return theme;
}
